'use client';

import { useEffect, useState, type CSSProperties, type ReactNode } from 'react';
import { AlertTriangle, Bell, CheckCircle2, Info, X, XCircle } from 'lucide-react';
import { Toaster, toast } from 'sonner';
import { consumeFlashToast } from '@/lib/flash-toast';

export type ToastVariant = 'success' | 'error' | 'warning' | 'info' | 'message';

export interface ToastAction {
	label: string;
	onClick: () => void;
}

interface NotifyOptions {
	description?: ReactNode;
	duration?: number;
	/** Stable id: firing again with the same id updates that toast instead of stacking a new one. */
	id?: string;
	/** Optional text button under the description. Clicking it does not close the toast. */
	action?: ToastAction;
	/** Called when the toast is dismissed: its close button or `toast.dismiss(id)`. */
	onDismiss?: () => void;
}

const VARIANT_ICON: Record<ToastVariant, ReactNode> = {
	success: <CheckCircle2 size={20} />,
	error: <XCircle size={20} />,
	warning: <AlertTriangle size={20} />,
	info: <Info size={20} />,
	message: <Bell size={20} />,
};

const VARIANT_ACCENT: Record<ToastVariant, string> = {
	success: '#16a34a',
	error: '#dc2626',
	warning: '#d97706',
	info: '#354857',
	message: '#354857',
};

// Action text is 13px on white, so it needs WCAG AA (4.5:1). The accents above are
// for the bar and the icon, and some fall short as text (warning #d97706 is 3.19:1,
// success #16a34a 3.30:1), hence a darker shade of the same hue per variant.
// On white: success 5.02, error 6.47, warning 5.02, info/message 9.48.
const VARIANT_ACTION_COLOR: Record<ToastVariant, string> = {
	success: '#15803d',
	error: '#b91c1c',
	warning: '#b45309',
	info: '#354857',
	message: '#354857',
};

// Styles are inlined (not in globals.css) on purpose: the toast renders in a
// portal via `toast.custom`, and keeping the look in the component makes it
// immune to global-CSS HMR desync, stylesheet ordering and specificity wars —
// the exact issues that kept the card unstyled. It also stays the single source
// of truth, reused verbatim by the admin catalog preview.
// Fixed width so every toast is the same size regardless of content (height
// still grows with the text). Shared by real toasts and the admin preview.
export const SIIV_TOAST_WIDTH = 380;

const CARD_STYLE: CSSProperties = {
	position: 'relative',
	display: 'flex',
	alignItems: 'flex-start',
	gap: 12,
	width: SIIV_TOAST_WIDTH,
	maxWidth: '100%',
	padding: '14px 16px 14px 18px',
	borderRadius: 10,
	border: '1px solid #e2e8f0',
	background: '#ffffff',
	color: '#1f2937',
	boxShadow: '0 10px 28px rgba(35, 58, 77, 0.14), 0 2px 6px rgba(35, 58, 77, 0.06)',
	overflow: 'hidden',
	fontFamily: 'var(--font-sans), system-ui, sans-serif',
	boxSizing: 'border-box',
};

interface SiivToastProps {
	variant: ToastVariant;
	title: ReactNode;
	description?: ReactNode;
	action?: ToastAction;
	onClose?: () => void;
}

function CloseButton({ onClose }: { onClose: () => void }) {
	const [hover, setHover] = useState(false);
	return (
		<button
			type="button"
			onClick={onClose}
			onMouseEnter={() => setHover(true)}
			onMouseLeave={() => setHover(false)}
			aria-label="Cerrar"
			style={{
				flexShrink: 0,
				display: 'flex',
				alignItems: 'center',
				justifyContent: 'center',
				height: 22,
				width: 22,
				margin: '-2px -4px 0 0',
				border: 0,
				borderRadius: 6,
				background: hover ? '#f1f5f9' : 'transparent',
				color: hover ? '#475569' : '#94a3b8',
				cursor: 'pointer',
			}}
		>
			<X size={15} />
		</button>
	);
}

function ActionButton({ action, color }: { action: ToastAction; color: string }) {
	const [hover, setHover] = useState(false);
	return (
		<button
			type="button"
			onClick={action.onClick}
			onMouseEnter={() => setHover(true)}
			onMouseLeave={() => setHover(false)}
			style={{
				display: 'inline-block',
				marginTop: 8,
				padding: 0,
				border: 0,
				background: 'transparent',
				color,
				fontSize: 13,
				fontWeight: 600,
				lineHeight: 1.35,
				textDecoration: hover ? 'underline' : 'none',
				cursor: 'pointer',
			}}
		>
			{action.label}
		</button>
	);
}

/**
 * The branded toast card. Single source of truth for the toast look — rendered
 * both by `notify` (via `toast.custom`, so sonner applies no styling of its own)
 * and by the admin catalog preview (/admin/toasts).
 */
export function SiivToast({ variant, title, description, action, onClose }: SiivToastProps) {
	const accent = VARIANT_ACCENT[variant];
	return (
		<div style={CARD_STYLE} role="status">
			<span
				aria-hidden
				style={{ position: 'absolute', top: 0, bottom: 0, left: 0, width: 4, background: accent }}
			/>
			<span
				aria-hidden
				style={{ flexShrink: 0, display: 'flex', alignItems: 'center', marginTop: 1, color: accent }}
			>
				{VARIANT_ICON[variant]}
			</span>
			{/* flex: 1 so the text column fills the fixed-width card and pushes the
			    close button to the right edge. Without it, short toasts (login) pack
			    left and leave the card half empty. minWidth: 0 keeps long text
			    wrapping instead of overflowing. */}
			<div style={{ flex: 1, minWidth: 0 }}>
				<p style={{ margin: 0, fontWeight: 600, fontSize: 14, color: '#1f2937' }}>{title}</p>
				{description ? (
					<p style={{ margin: '2px 0 0', fontSize: 13, lineHeight: 1.35, color: '#64748b' }}>
						{description}
					</p>
				) : null}
				{action ? <ActionButton action={action} color={VARIANT_ACTION_COLOR[variant]} /> : null}
			</div>
			{onClose ? <CloseButton onClose={onClose} /> : null}
		</div>
	);
}

function show(variant: ToastVariant, title: ReactNode, options?: NotifyOptions) {
	return toast.custom(
		(id) => (
			<SiivToast
				variant={variant}
				title={title}
				description={options?.description}
				action={options?.action}
				onClose={() => toast.dismiss(id)}
			/>
		),
		// `id` only when given: sonner's `custom` spreads these options AFTER the id it
		// generated, so an explicit `id: undefined` would replace it and the close
		// button would dismiss a toast that no longer exists.
		{
			duration: options?.duration ?? 5000,
			...(options?.id ? { id: options.id } : {}),
			...(options?.onDismiss ? { onDismiss: options.onDismiss } : {}),
		},
	);
}

/**
 * SIIV branded toaster. Mounted once at the root layout so toasts render on
 * every route — including the auth/onboarding pages, which live outside the
 * app shell. `expand` keeps every toast fully legible: a new toast pushes the
 * others up instead of collapsing them behind (which rendered as blank cards).
 */
export function BrandToaster() {
	// Replay a toast queued for the next page (see flash-toast.ts). Every flash is
	// followed by a hard navigation (login + both logouts use window.location), so
	// the toaster always remounts on a freshly-loaded document and this mount effect
	// fires the queued toast. (The toast card width is handled in globals.css, not
	// here — custom toasts are unstyled and miss sonner's own width rule.)
	useEffect(() => {
		const flash = consumeFlashToast();
		if (flash) {
			show(flash.variant, flash.title, { description: flash.description });
		}
	}, []);

	return (
		<Toaster
			position="bottom-right"
			expand
			visibleToasts={4}
			duration={5000}
			gap={10}
			style={{ ['--width' as string]: '380px' }}
		/>
	);
}

/**
 * Brand-flavoured wrapper around sonner. Always use these helpers so every
 * notification renders the branded `SiivToast` card consistently.
 */
export const notify = {
	success: (title: ReactNode, options?: NotifyOptions) => show('success', title, options),
	error: (title: ReactNode, options?: NotifyOptions) => show('error', title, options),
	info: (title: ReactNode, options?: NotifyOptions) => show('info', title, options),
	warning: (title: ReactNode, options?: NotifyOptions) => show('warning', title, options),
	message: (title: ReactNode, options?: NotifyOptions) => show('message', title, options),
};
